import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  CheckCircle,
  Copy,
  Download,
  MoreVertical,
  Play,
  RotateCcw,
  Settings,
  Trash2,
  XCircle,
  Send,
  MessageSquare,
  Image,
  Video,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

const getFileUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BASE_URL}${url}`;
};

const RecceModule = ({ project }) => {
  const navigate = useNavigate();

  const projectId =
    project?._id || project?.id || localStorage.getItem("selectedProjectId");

  const projectName =
    project?.name || localStorage.getItem("selectedProjectName") || "SHIV";

  const [sections, setSections] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState("");
  const [recceStatus, setRecceStatus] = useState("Submitted");
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const menuRef = useRef(null);

  const token = localStorage.getItem("token");
  const userName =
    localStorage.getItem("userName") ||
    JSON.parse(localStorage.getItem("user") || "{}")?.name ||
    "User";

  const axiosConfig = {
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
    },
  };

  const loadRecce = async () => {
    if (!projectId) return;

    try {
      setLoading(true);

      const res = await axios.get(
        `${BASE_URL}/api/drawing-recce/${projectId}`,
        axiosConfig,
      );

      setSections(res.data.sections || []);
      setRecceStatus(res.data.status || "Submitted");

      if (res.data.sections?.length > 0) {
        setActiveSectionId((prev) => prev || res.data.sections[0]._id);
      }
    } catch (error) {
      console.error("Recce load error:", error);
      alert(error.response?.data?.message || "Failed to load recce");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecce();
  }, [projectId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeSection = useMemo(
    () => sections.find((section) => section._id === activeSectionId),
    [sections, activeSectionId],
  );

  const handleUpload = async (type, event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length || !activeSectionId) return;

    try {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append("files", file);
      });

      formData.append("type", type);
      formData.append("uploadedBy", userName);

      const res = await axios.post(
        `${BASE_URL}/api/drawing-recce/${projectId}/sections/${activeSectionId}/upload`,
        formData,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
        },
      );

      setSections(res.data.sections || []);
      event.target.value = "";
    } catch (error) {
      console.error("Upload error:", error);
      alert(error.response?.data?.message || "File upload failed");
    }
  };

  const addSection = async () => {
    const sectionName = window.prompt("Enter section name");
    if (!sectionName?.trim()) return;

    try {
      const res = await axios.post(
        `${BASE_URL}/api/drawing-recce/${projectId}/sections`,
        { name: sectionName.trim() },
        axiosConfig,
      );

      setSections(res.data.sections || []);

      const lastSection = res.data.sections?.[res.data.sections.length - 1];
      if (lastSection?._id) setActiveSectionId(lastSection._id);
    } catch (error) {
      console.error("Add section error:", error);
      alert(error.response?.data?.message || "Failed to add section");
    }
  };

  const updateStatus = async (statusValue, recceStatusValue) => {
    try {
      const res = await axios.put(
        `${BASE_URL}/api/drawing-recce/${projectId}/status`,
        {
          status: statusValue,
          recceStatus: recceStatusValue,
        },
        axiosConfig,
      );

      setRecceStatus(res.data.status);
      setShowMenu(false);
    } catch (error) {
      console.error("Status update error:", error);
      alert(error.response?.data?.message || "Failed to update status");
    }
  };

  const deleteRecce = async () => {
    const ok = window.confirm("Are you sure you want to delete this recce?");
    if (!ok) return;

    try {
      await axios.delete(
        `${BASE_URL}/api/drawing-recce/${projectId}`,
        axiosConfig,
      );

      setSections([]);
      setActiveSectionId("");
      setShowMenu(false);
      await loadRecce();
    } catch (error) {
      console.error("Delete recce error:", error);
      alert(error.response?.data?.message || "Failed to delete recce");
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert("Shareable link copied");
      setShowMenu(false);
    } catch {
      alert("Failed to copy link");
    }
  };

  const addComment = async () => {
    if (!commentText.trim()) {
      alert("Please write comment");
      return;
    }

    if (!projectId || !activeSectionId) {
      alert("Please select section first");
      return;
    }

    try {
      setCommentLoading(true);

      const res = await axios.post(
        `${BASE_URL}/api/drawing-recce/${projectId}/sections/${activeSectionId}/comments`,
        {
          text: commentText.trim(),
          createdBy: userName,
        },
        axiosConfig,
      );

      setSections(res.data.sections || []);
      setCommentText("");
    } catch (error) {
      console.error("Comment save error:", error);
      alert(error.response?.data?.message || "Failed to save comment");
    } finally {
      setCommentLoading(false);
    }
  };

  const deleteComment = async (commentId) => {
    const ok = window.confirm("Delete this comment?");
    if (!ok) return;

    try {
      const res = await axios.delete(
        `${BASE_URL}/api/drawing-recce/${projectId}/sections/${activeSectionId}/comments/${commentId}`,
        axiosConfig,
      );

      setSections(res.data.sections || []);
    } catch (error) {
      console.error("Comment delete error:", error);
      alert(error.response?.data?.message || "Failed to delete comment");
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading Recce...</div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.topHeader}>
        <button style={styles.backBtn} onClick={() => navigate(-1)}>
          ← Back
        </button>

        <div style={styles.headingBox}>
          <h1 style={styles.mainHeading}>Recce</h1>
          <h2 style={styles.projectName}>Project Name : {projectName}</h2>
        </div>

        <div style={{ width: 95 }} />
      </div>

      <div style={styles.toolbar}>
        <div style={styles.selectWrap}>
          <span style={styles.label}>Select Recce :</span>
          <select style={styles.select}>
            <option>Recce | Site Survey</option>
          </select>
        </div>

        <div style={styles.statusLine}>
          Status : <span style={styles.statusBadge}>{recceStatus}</span>
          <RotateCcw size={14} />
        </div>

        <div style={styles.rightActions}>
          <button
            style={styles.approveBtn}
            onClick={() => updateStatus("Approved", "Approved")}
          >
            <CheckCircle size={16} />
            Mark Approve
          </button>

          <div style={styles.menuWrap} ref={menuRef}>
            <button
              type="button"
              style={styles.moreBtn}
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu((prev) => !prev);
              }}
            >
              <MoreVertical size={24} />
            </button>

            {showMenu && (
              <div style={styles.dropdown}>
                <button
                  style={styles.menuItem}
                  onClick={() => updateStatus("Approved", "Approved")}
                >
                  <CheckCircle size={17} />
                  Mark Approve
                </button>

                <button
                  style={styles.menuItem}
                  onClick={() => updateStatus("Submitted", "Recce Pending")}
                >
                  <XCircle size={17} />
                  Mark Un-approve
                </button>

                <button style={styles.menuItem} onClick={copyShareLink}>
                  <Copy size={17} />
                  Copy Shareable Link
                </button>

                <button style={styles.menuItem} onClick={() => window.print()}>
                  <Download size={17} />
                  Download Report
                </button>

                <button style={styles.menuItem}>
                  <Settings size={17} />
                  Current Recce Settings
                </button>

                <button style={styles.deleteMenuItem} onClick={deleteRecce}>
                  <Trash2 size={17} />
                  Delete Recce
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={styles.body}>
        <aside style={styles.sidebar}>
          <div>
            <h3 style={styles.sidebarTitle}>Sections</h3>

            {sections.map((section) => (
              <button
                key={section._id}
                onClick={() => setActiveSectionId(section._id)}
                style={{
                  ...styles.sectionBtn,
                  background:
                    section._id === activeSectionId ? "#e0f2fe" : "transparent",
                  color:
                    section._id === activeSectionId ? "#075985" : "#334155",
                  fontWeight: section._id === activeSectionId ? 800 : 600,
                }}
              >
                {section.name}
              </button>
            ))}

            {sections.length === 0 && (
              <div style={styles.emptySmall}>No section added</div>
            )}
          </div>

          <button onClick={addSection} style={styles.addSectionBtn}>
            + Add New Section
          </button>
        </aside>

        <main style={styles.content}>
          <div style={styles.sectionHeader}>
            <div>
              <h3 style={styles.sectionTitle}>
                {activeSection?.name || "No Section Selected"}
              </h3>
              <p style={styles.sectionSubTitle}>
                Upload photos, videos and add comments for this recce section.
              </p>
            </div>
          </div>

          <div style={styles.mainGrid}>
            <div style={styles.leftPanel}>
              <MediaSection
                title="Photos"
                type="photo"
                icon={<Image size={18} />}
                items={activeSection?.photos || []}
                onUpload={handleUpload}
                showViewAll
                setPreviewImage={setPreviewImage}
              />

              <MediaSection
                title="Videos"
                type="video"
                icon={<Video size={18} />}
                items={activeSection?.videos || []}
                onUpload={handleUpload}
              />
            </div>

            <CommentSection
              comments={activeSection?.comments || []}
              commentText={commentText}
              setCommentText={setCommentText}
              addComment={addComment}
              deleteComment={deleteComment}
              commentLoading={commentLoading}
            />
          </div>
        </main>
      </div>

      {previewImage && (
        <div style={styles.previewOverlay} onClick={() => setPreviewImage("")}>
          <button
            style={styles.previewCloseBtn}
            onClick={() => setPreviewImage("")}
          >
            <X size={24} />
          </button>

          <img
            src={previewImage}
            alt="Preview"
            style={styles.previewImage}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

const MediaSection = ({
  title,
  type,
  icon,
  items,
  onUpload,
  showViewAll,
  setPreviewImage,
}) => {
  return (
    <div style={styles.mediaSection}>
      <div style={styles.mediaHeader}>
        <h4 style={styles.mediaTitle}>
          {icon}
          {title}
          <span style={{ color: "#ef4444" }}>*</span>
        </h4>

        <div style={styles.mediaActions}>
          <label style={styles.uploadBtn}>
            + Upload Files
            <input
              type="file"
              multiple
              accept={type === "photo" ? "image/*" : "video/*"}
              onChange={(e) => onUpload(type, e)}
              style={{ display: "none" }}
            />
          </label>

          {showViewAll && (
            <span style={styles.viewAll}>View all {items.length} Images</span>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div style={styles.emptyBox}>No {title.toLowerCase()} uploaded.</div>
      ) : (
        <div style={styles.mediaGrid}>
          {items.map((item) => {
            const src = getFileUrl(item.fileUrl);

            return (
              <div key={item._id || item.fileUrl} style={styles.mediaCard}>
                {type === "video" ? (
                  <>
                    <video src={src} style={styles.mediaImg} controls />
                    <div style={styles.playCircle}>
                      <Play size={28} fill="#334155" color="#334155" />
                    </div>
                  </>
                ) : (
                  <img
                    src={src}
                    alt={item.fileName}
                    style={{
                      ...styles.mediaImg,
                      cursor: "pointer",
                    }}
                    onClick={() => setPreviewImage(src)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CommentSection = ({
  comments,
  commentText,
  setCommentText,
  addComment,
  deleteComment,
  commentLoading,
}) => {
  return (
    <aside style={styles.commentPanel}>
      <div style={styles.commentHeader}>
        <h3 style={styles.commentTitle}>
          <MessageSquare size={18} />
          Comments
        </h3>
        <p style={styles.commentSubTitle}>Section-wise recce discussion</p>
      </div>

      <div style={styles.commentList}>
        {comments.length === 0 ? (
          <div style={styles.noCommentBox}>No comments yet.</div>
        ) : (
          comments.map((comment) => (
            <div key={comment._id} style={styles.commentCard}>
              <div style={styles.commentTop}>
                <strong style={styles.commentUser}>
                  {comment.createdBy || "User"}
                </strong>

                <button
                  style={styles.commentDeleteBtn}
                  onClick={() => deleteComment(comment._id)}
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <p style={styles.commentText}>{comment.text}</p>

              <span style={styles.commentDate}>
                {comment.createdAt
                  ? new Date(comment.createdAt).toLocaleString()
                  : ""}
              </span>
            </div>
          ))
        )}
      </div>

      <div style={styles.commentInputBox}>
        <textarea
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Write your comment..."
          style={styles.commentTextarea}
        />

        <button
          style={{
            ...styles.sendBtn,
            opacity: commentLoading ? 0.65 : 1,
          }}
          onClick={addComment}
          disabled={commentLoading}
        >
          <Send size={16} />
          {commentLoading ? "Sending..." : "Send Comment"}
        </button>
      </div>
    </aside>
  );
};

const styles = {
  loading: {
    padding: 30,
    background: "#fff",
    fontWeight: 700,
  },

  page: {
    background: "#fff",
    border: "1px solid #d1d5db",
    borderRadius: 14,
    height: "calc(100vh - 40px)",
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
    position: "relative",
  },

  topHeader: {
    height: 96,
    background: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 22px",
  },

  backBtn: {
    height: 38,
    padding: "0 16px",
    border: "1px solid #d1d5db",
    borderRadius: 10,
    background: "#ffffff",
    color: "#111827",
    fontWeight: 800,
    cursor: "pointer",
  },

  headingBox: {
    textAlign: "center",
    flex: 1,
  },

  mainHeading: {
    margin: 0,
    fontSize: 34,
    fontWeight: 900,
    color: "#0f172a",
  },

  projectName: {
    margin: "8px 0 0",
    fontSize: 26,
    fontWeight: 900,
    color: "#343A76",
    textTransform: "uppercase",
  },

  toolbar: {
    height: 62,
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    gap: 22,
    padding: "0 16px",
    background: "#ffffff",
    position: "relative",
    zIndex: 20,
  },

  selectWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  label: {
    color: "#64748b",
    fontSize: 14,
    fontWeight: 600,
  },

  select: {
    width: 270,
    height: 36,
    border: "1px solid #cbd5e1",
    borderRadius: 9,
    fontWeight: 700,
    padding: "0 10px",
    background: "#fff",
    color: "#0f172a",
  },

  statusLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 700,
    color: "#334155",
  },

  statusBadge: {
    color: "#0891b2",
    background: "#ecfeff",
    border: "1px solid #a5f3fc",
    padding: "4px 10px",
    borderRadius: 999,
  },

  rightActions: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  approveBtn: {
    height: 36,
    border: "1px solid #fb923c",
    color: "#f97316",
    background: "#fff7ed",
    borderRadius: 10,
    padding: "0 15px",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    gap: 7,
    cursor: "pointer",
  },

  menuWrap: {
    position: "relative",
    zIndex: 1000,
  },

  moreBtn: {
    width: 38,
    height: 38,
    border: "1px solid #e5e7eb",
    background: "#fff",
    borderRadius: 10,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
  },

  dropdown: {
    position: "absolute",
    right: 0,
    top: 44,
    width: 245,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    boxShadow: "0 18px 40px rgba(15,23,42,0.22)",
    zIndex: 9999,
    overflow: "hidden",
  },

  menuItem: {
    width: "100%",
    minHeight: 44,
    border: 0,
    borderBottom: "1px solid #f1f5f9",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 14px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    textAlign: "left",
    color: "#334155",
  },

  deleteMenuItem: {
    width: "100%",
    minHeight: 44,
    border: 0,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 14px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "left",
    color: "#dc2626",
  },

  body: {
    display: "flex",
    height: "calc(100% - 158px)",
    overflow: "hidden",
  },

  sidebar: {
    width: 225,
    borderRight: "1px solid #e5e7eb",
    padding: 14,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "#f8fafc",
    overflowY: "auto",
  },

  sidebarTitle: {
    margin: "0 0 12px",
    fontSize: 15,
    color: "#0f172a",
  },

  sectionBtn: {
    width: "100%",
    border: 0,
    textAlign: "left",
    padding: "11px 12px",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 14,
    marginBottom: 6,
  },

  emptySmall: {
    padding: 14,
    color: "#94a3b8",
    fontSize: 13,
    border: "1px dashed #cbd5e1",
    borderRadius: 10,
    background: "#fff",
  },

  addSectionBtn: {
    border: "1px dashed #ef4444",
    background: "#fff",
    color: "#ef4444",
    fontWeight: 800,
    textAlign: "center",
    padding: 12,
    cursor: "pointer",
    fontSize: 14,
    borderRadius: 10,
  },

  content: {
    flex: 1,
    overflow: "hidden",
    background: "#fff",
  },

  sectionHeader: {
    height: 70,
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    padding: "0 22px",
    background: "#ffffff",
  },

  sectionTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 900,
    color: "#0f172a",
  },

  sectionSubTitle: {
    margin: "5px 0 0",
    fontSize: 13,
    color: "#64748b",
  },

  mainGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 360px",
    height: "calc(100% - 70px)",
    overflow: "hidden",
  },

  leftPanel: {
    padding: "0 0 25px",
    overflowY: "auto",
  },

  mediaSection: {
    padding: "20px 22px 8px",
  },

  mediaHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: 14,
  },

  mediaTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 900,
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  mediaActions: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  uploadBtn: {
    color: "#ef4444",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 14,
  },

  viewAll: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 600,
  },

  mediaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(165px, 165px))",
    gap: 20,
  },

  mediaCard: {
    width: 165,
    height: 155,
    borderRadius: 12,
    overflow: "hidden",
    background: "#e5e7eb",
    position: "relative",
    boxShadow: "0 8px 18px rgba(15,23,42,0.08)",
  },

  mediaImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  playCircle: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: 58,
    height: 58,
    borderRadius: "50%",
    border: "4px solid #334155",
    background: "rgba(255,255,255,0.65)",
    display: "grid",
    placeItems: "center",
    pointerEvents: "none",
  },

  emptyBox: {
    border: "1px dashed #cbd5e1",
    borderRadius: 12,
    padding: 28,
    color: "#64748b",
    background: "#f8fafc",
    fontWeight: 600,
  },

  commentPanel: {
    borderLeft: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },

  commentHeader: {
    padding: 18,
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
  },

  commentTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 900,
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  commentSubTitle: {
    margin: "5px 0 0",
    fontSize: 13,
    color: "#64748b",
  },

  commentList: {
    flex: 1,
    padding: 16,
    overflowY: "auto",
  },

  noCommentBox: {
    border: "1px dashed #cbd5e1",
    background: "#fff",
    borderRadius: 12,
    padding: 18,
    color: "#64748b",
    fontWeight: 600,
    textAlign: "center",
  },

  commentCard: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 13,
    marginBottom: 12,
    boxShadow: "0 4px 12px rgba(15,23,42,0.05)",
  },

  commentTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  commentUser: {
    fontSize: 14,
    color: "#0f172a",
  },

  commentDeleteBtn: {
    border: 0,
    background: "transparent",
    color: "#ef4444",
    cursor: "pointer",
  },

  commentText: {
    margin: "8px 0",
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.45,
  },

  commentDate: {
    fontSize: 11,
    color: "#94a3b8",
  },

  commentInputBox: {
    borderTop: "1px solid #e5e7eb",
    padding: 14,
    background: "#ffffff",
  },

  commentTextarea: {
    width: "100%",
    minHeight: 76,
    resize: "none",
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    padding: 12,
    outline: "none",
    fontSize: 14,
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  sendBtn: {
    marginTop: 10,
    width: "100%",
    height: 40,
    border: 0,
    borderRadius: 12,
    background: "#0ea5e9",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },

  previewOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.85)",
    zIndex: 99999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 30,
  },

  previewImage: {
    maxWidth: "92%",
    maxHeight: "92%",
    borderRadius: 16,
    objectFit: "contain",
    boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
  },

  previewCloseBtn: {
    position: "fixed",
    top: 22,
    right: 28,
    width: 44,
    height: 44,
    borderRadius: "50%",
    border: 0,
    background: "#fff",
    color: "#111827",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    zIndex: 100000,
  },
};

export default RecceModule;
